import { Box, Container, Stack, Typography } from "@mui/material";
import type { Theme } from "@mui/material";
import type { ReactNode } from "react";

export type SectionVariant = "plain" | "cream" | "blush" | "pearl" | "gold" | "dark";

interface SectionProps {
  id: string;
  title?: string;
  subtitle?: string;
  eyebrow?: string;
  children: ReactNode;
  dense?: boolean;
  variant?: SectionVariant;
  sx?: object;
}

function variantBackground(variant: SectionVariant, theme: Theme): string | undefined {
  const isDark = theme.palette.mode === "dark";
  switch (variant) {
    case "plain":
      return undefined;
    case "cream":
      return isDark
        ? "linear-gradient(180deg, #171513 0%, #1B1815 100%)"
        : "linear-gradient(180deg, #FAF4EC 0%, #F6EEE2 100%)";
    case "blush":
      return isDark
        ? "linear-gradient(180deg, #1A1613 0%, #1F1A15 100%)"
        : "linear-gradient(180deg, #FBEFE6 0%, #F6E3D4 100%)";
    case "pearl":
      return isDark
        ? "linear-gradient(180deg, #1A1714 0%, #211D18 100%)"
        : "linear-gradient(180deg, #FFFDFB 0%, #FBF5EC 100%)";
    case "gold":
      return isDark
        ? "radial-gradient(1200px 600px at 20% 10%, rgba(201,163,90,0.10), transparent 60%), linear-gradient(180deg, #171513 0%, #1C1814 100%)"
        : "radial-gradient(1200px 600px at 20% 10%, rgba(201,163,90,0.18), transparent 60%), linear-gradient(180deg, #FFFBF3 0%, #F8ECD6 100%)";
    case "dark":
      return isDark
        ? "linear-gradient(180deg, #120F0D 0%, #17130F 100%)"
        : "linear-gradient(180deg, #241E18 0%, #2C2419 100%)";
  }
}

export function Section({
  id,
  title,
  subtitle,
  eyebrow,
  children,
  dense,
  variant = "plain",
  sx,
}: SectionProps) {
  const isDarkVariant = variant === "dark";

  return (
    <Box
      component="section"
      id={id}
      sx={{
        position: "relative",
        py: dense ? { xs: 7, md: 9 } : { xs: 9, md: 13 },
        scrollMarginTop: 88,
        background: (theme: Theme) => variantBackground(variant, theme),
        color: isDarkVariant ? "#F6EFE4" : undefined,
        ...sx,
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {(title || eyebrow || subtitle) && (
          <Stack spacing={1.5} sx={{ mb: { xs: 5, md: 7 }, maxWidth: 760 }}>
            {eyebrow && (
              <Typography
                variant="overline"
                color={isDarkVariant ? "inherit" : "primary"}
                sx={{
                  letterSpacing: 3.5,
                  fontWeight: 700,
                  color: isDarkVariant ? "#E8C789" : undefined,
                }}
              >
                {eyebrow}
              </Typography>
            )}
            {title && (
              <Typography variant="h3" component="h2">
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="body1"
                color={isDarkVariant ? "inherit" : "text.secondary"}
                sx={{ maxWidth: 680, opacity: isDarkVariant ? 0.85 : undefined }}
              >
                {subtitle}
              </Typography>
            )}
          </Stack>
        )}
        {children}
      </Container>
    </Box>
  );
}
